import useCreateMFACode from "api/mutations/useCreateMFACode";
import useSubmitMFACode from "api/mutations/useSubmitMFACode";
import { useGetPerson } from "api/queries/useGetPerson";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { sanitizeReturnUrl } from "utils/sanitizeReturnUrl";
import useRequiresSignin from "utils/useRequiresSignin";
import useSnackbar from "utils/useSnackbar";

interface MFAFormValues {
	code: string;
}

const useController = () => {
	const { data: person } = useGetPerson();

	const { mutateAsync: createMFACode } = useCreateMFACode();
	const { mutateAsync: submitMFACode } = useSubmitMFACode();

	useRequiresSignin();
	const [searchParams] = useSearchParams();
	const sanitizedReturnUrl = sanitizeReturnUrl(searchParams.get("return_url"));
	const redirectUrl = sanitizedReturnUrl?.url || "/";
	const navigate = useNavigate();
	const { handleError } = useSnackbar();
	const [errorMessage, setErrorMessage] = useState("");

	/** send an mfa code by email if user lands on this page and they aren't mfa authenticated  */
	useEffect(() => {
		if (person && !person.mfaAuthenticated) {
			const controller = new AbortController();
			createMFACode(controller).catch((error) => handleError(error));

			return () => controller.abort();
		}
	}, [createMFACode, handleError, person]);

	/** force user forward once they have their mfa authenticated (deep links and successful submits) */
	useEffect(() => {
		if (!person?.mfaAuthenticated) return;

		if (sanitizedReturnUrl?.isAbsolute) {
			window.location.assign(redirectUrl);
			return;
		}

		navigate(redirectUrl);
	}, [
		navigate,
		person?.mfaAuthenticated,
		redirectUrl,
		sanitizedReturnUrl?.isAbsolute,
	]);

	const handleSubmitMFACode = async (data: FieldValues) => {
		const { code } = data as MFAFormValues;
		setErrorMessage("");

		const response = await submitMFACode({ params: { code } });
		if (!response.success && !response.error) {
			setErrorMessage("Invalid code.");
		}
	};

	return { errorMessage, handleSubmitMFACode };
};

export default useController;
